// slice/authSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { API_V1_BASE_URL } from '../config/api';
import type{LoginResponse} from "../types/auth";

// Real login — calls POST /api/v1/auth/login and stores the token,
// refresh token, user, and permissions returned by the backend.
export const login = createAsyncThunk(
    'auth/login',
    async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
        try {
            const response = await fetch(`${API_V1_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            const result: LoginResponse = await response.json();

            if (!response.ok || !result.success) {
                return rejectWithValue(result.message || 'Login failed');
            }

            const { accessToken, refreshToken, user } = result.data;

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(user));

            return { user, accessToken, refreshToken };
        } catch (err) {
            return rejectWithValue('Something went wrong. Please try again.');
        }
    }
);

export const logout = createAsyncThunk('auth/logout', async () => {
    const accessToken = localStorage.getItem('accessToken');

    try {
        if (accessToken) {
            await fetch(`${API_V1_BASE_URL}/auth/logout`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
        }
    } finally {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
    }
});

import type { User } from '../types/auth';

interface AuthState {
    user: User | null;
    token: string | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

interface SessionPayload {
    user: User;
    accessToken: string;
    refreshToken: string;
}

// Drop any session left over from demo mode; its fake token is rejected by the backend
if (localStorage.getItem('accessToken')?.startsWith('demo-')) {
    ['accessToken', 'refreshToken', 'user'].forEach((k) => localStorage.removeItem(k));
}

const initialState: AuthState = {
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    token: localStorage.getItem('accessToken'),
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    isAuthenticated: !!localStorage.getItem('accessToken'),
    isLoading: false,
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setSession: (state, action: PayloadAction<SessionPayload>) => {
            state.user = action.payload.user;
            state.token = action.payload.accessToken;
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.isAuthenticated = true;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
                state.token = action.payload.accessToken;
                state.accessToken = action.payload.accessToken;
                state.refreshToken = action.payload.refreshToken;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as string) || 'Login failed';
            })
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                 state.token = null;
                state.accessToken = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
            });
    },
});

export const { clearError, setSession } = authSlice.actions;
export default authSlice.reducer;

